import { useEffect, useState, useRef, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const faqs = [
  {
    q: '韩漫网站上的漫画都是免费阅读的吗？',
    a: '是的，本站所有韩漫资源均为免费阅读。我们致力于为漫迷提供优质的韩漫免费无遮阅读体验，让每一位读者都能畅享韩国漫画的魅力。',
  },
  {
    q: '什么是下拉双男主韩漫？',
    a: '下拉双男主是指采用垂直滚动阅读方式、以两位男性主角为核心的韩漫作品。这种形式特别适合在手机上阅读，同时双男主设定也让故事具有更丰富的情感层次和叙事张力。',
  },
  {
    q: '韩漫免费无遮漫画是什么意思？',
    a: '韩漫免费无遮漫画指的是可以免费阅读且画面完整无遮挡的漫画内容。读者可以完整欣赏每一页的原画，画面的细节和美感得到充分展现，获得最佳的阅读体验。',
  },
  {
    q: '如何找到好看的漫画韩漫？',
    a: '您可以通过本站的分类导航浏览不同题材的韩漫作品。我们还提供了热门推荐和最新更新栏目，帮助您快速发现好评如潮的优质韩漫。双男主、爱情恋爱、玄幻科幻等多种类型任您选择。',
  },
  {
    q: '漫画的更新频率是怎样的？',
    a: '我们会在第一时间同步更新最新的韩漫章节。大部分连载作品的更新频率为每周一到两次，具体更新时间因作品而异。您可以通过"最近更新"栏目追踪最新动态。',
  },
  {
    q: '本站支持哪些设备阅读？',
    a: '本站采用响应式设计，完美适配电脑、平板和手机等各类设备。无论您在什么设备上阅读，都能获得流畅舒适的韩漫阅读体验。',
  },
];

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = '关于我们 - 韩漫免费阅读';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSuccess(true);
      form.reset();
      setFormSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const resp = await fetch('https://readdy.ai/api/form/d9pej6mb4qjhggaepsk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await resp.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (resp.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    } finally {
      setFormSubmitting(false);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 py-8">
            <div className="max-w-[960px] mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">关于我们</h1>
            </div>
          </section>

          <section className="max-w-[960px] mx-auto px-4 md:px-6 py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">品牌介绍</h2>
                  <p className="text-foreground-700 leading-relaxed text-sm">
                    韩漫免费阅读平台成立于2020年，是国内领先的韩漫在线阅读网站。我们以<strong>"让每一个人都能免费享受优质韩漫"</strong>为使命，汇集了涵盖下拉双男主、免费无遮、爱情恋爱、玄幻科幻等多种类型的韩国漫画资源。
                  </p>
                  <p className="text-foreground-700 leading-relaxed text-sm mt-3">
                    经过多年的发展，我们已经成为拥有超过<strong>2000部漫画作品</strong>和<strong>50万名活跃读者</strong>的韩漫阅读社区。本站所有韩漫均支持免费阅读，无需注册即可畅享完整内容。韩漫网站支持免费阅读看漫画下拉双男主，好看的漫画韩漫免费看，并致力于为用户提供最佳的韩漫免费无遮漫画阅读体验。
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">我们的优势</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有韩漫资源免费阅读，无任何隐藏费用' },
                      { icon: 'ri-flashlight-line', title: '极速更新', desc: '第一时间同步最新章节，追更不等待' },
                      { icon: 'ri-smartphone-line', title: '多端适配', desc: '电脑、平板、手机都能获得流畅阅读体验' },
                      { icon: 'ri-bookmark-line', title: '分类齐全', desc: '八大分类覆盖所有韩漫类型，轻松找到心头好' },
                    ].map((item) => (
                      <div key={item.title} className="flex gap-3 p-4 rounded-xl bg-background-100">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <div>
                          <h4 className="font-heading text-sm font-semibold text-foreground-950">{item.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">用户口碑</h2>
                  <div className="space-y-4">
                    {[
                      { name: '漫迷小王', rating: 5, text: '韩漫免费阅读平台真的是宝藏！下拉双男主类型的漫画太全了，每天都要来刷一刷。完全免费无遮的阅读体验，强烈推荐！' },
                      { name: '资深韩漫读者', rating: 5, text: '在这个平台看了快两年了，更新速度超快，画质也清晰。好看的漫画韩漫免费看，比其他平台良心太多了。' },
                      { name: '新入坑漫迷', rating: 4, text: '朋友推荐的这个平台，分类清晰很容易找到喜欢的作品。韩漫免费无遮漫画资源很丰富，已经安利给好几个朋友了。' },
                    ].map((review, i) => (
                      <div key={i} className="p-4 rounded-xl bg-background-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-heading text-sm font-semibold text-foreground-950">{review.name}</span>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: review.rating }).map((_, j) => (
                              <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-20 space-y-6">
                  <div className="p-5 rounded-xl bg-background-100">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">联系方式</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-mail-line text-primary-600"></i>
                        <span>contact@manhuafree.com</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-customer-service-line text-primary-600"></i>
                        <span>在线客服：工作日 9:00-18:00</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 rounded-xl bg-background-100">
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">给我们留言</h3>
                    {formSuccess ? (
                      <div className="text-center py-6">
                        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                          <i className="ri-check-line text-xl"></i>
                        </div>
                        <p className="mt-3 text-sm text-foreground-700 font-medium">提交成功！</p>
                        <p className="text-xs text-foreground-500 mt-1">感谢您的留言，我们会尽快回复。</p>
                      </div>
                    ) : (
                      <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="d9pej6mb4qjhggaepsk0" className="space-y-3">
                        <div>
                          <input
                            type="text"
                            name="name"
                            required
                            placeholder="您的姓名"
                            className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="您的邮箱"
                            className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            placeholder="您的留言（500字以内）"
                            className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          />
                        </div>
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="antibot-field" />
                        {formError && (
                          <p className="text-xs text-red-600">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={formSubmitting}
                          className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                        >
                          {formSubmitting ? '提交中...' : '提交留言'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-100 py-14">
            <div className="max-w-[960px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">常见问题</h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <h4 className="font-heading text-sm font-semibold text-foreground-950 pr-4">{faq.q}</h4>
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                        openFaq === i ? 'bg-primary-500 text-background-50 rotate-180' : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 韩漫免费阅读',
            description: '韩漫免费阅读平台是国内领先的韩漫在线阅读网站，提供下拉双男主、免费无遮等多种类型的韩国漫画。',
            url: 'https://www.example.com/about',
            isPartOf: { '@type': 'WebSite', name: '韩漫免费阅读', url: 'https://www.example.com/' },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
    </>
  );
}